export enum ResourceBookingRule {
  BOOKABLE = 'bookableRule',
  ASSIGNMENT = 'assignmentRule',
  ADVANCED_BOOKING = 'AdvancedBookingLimitRule',
  BOOKING_HOUR = 'bookingHourRule',
  RESERVATION_TYPE = 'ReservationTypeStrategy',
}

export type ResourceBookableRuleType = {
  key: ResourceBookingRule.BOOKABLE;
  properties: {
    bookable: boolean;
  };
};

export type ResourceAssignmentRuleType = {
  key: ResourceBookingRule.ASSIGNMENT;
  properties: unknown;
};

export type ResourceAdvancedBookingRuleType =
  | {
      key: ResourceBookingRule.ADVANCED_BOOKING;
      properties: {
        daysInAdvance: number;
        bookingWindowOpensAt: string;
      };
    }
  | {
      key: ResourceBookingRule.ADVANCED_BOOKING;
      properties: {
        daysInAdvance: null;
        bookingWindowOpensAt: null;
      };
    };

export type ResourceBookingHourRuleType = {
  key: ResourceBookingRule.BOOKING_HOUR;
  properties: {
    bookingHours: {
      active: boolean;
      dayOfWeek: 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
      startTime: string;
      endTime: string;
    }[];
  };
};

export type ResourceReservationTypeRuleType =
  | {
      key: ResourceBookingRule.RESERVATION_TYPE;
      type: 'FullDayReservationStrategy';
      properties: Record<string, unknown>;
    }
  | {
      key: ResourceBookingRule.RESERVATION_TYPE;
      type: 'PartialDayReservationStrategy';
      properties: Record<string, unknown>;
    };

type DisabledMissedCheckInStrategy = {
  type: 'DisabledMissedCheckInStrategy';
  properties: Record<string, unknown>;
};

type SetTimeOfDayMissedCheckInStrategy = {
  type: 'SetTimeOfDayMissedCheckInStrategy';
  properties: {
    checkInBy: string;
  };
};

// This strategy is also not used anywhere
// type GracePeriodMissedCheckInStrategy = {
//   type: 'GracePeriodMissedCheckInStrategy';
//   properties: {
//     gracePeriodMinutes: number;
//   };
// };

// This won't be used or supported until partial day booking is available for resources
// type CompositeMissedCheckInStrategy = {
//   type: 'CompositeMissedCheckInStrategy';
//   properties: {
//     setTimeOfDay: {
//       checkInBy: string;
//     };
//     gracePeriod: {
//       gracePeriodMinutes: number;
//     };
//   };
// };

export type ResourceMissedCheckInStrategyType = { key: 'MissedCheckInStrategy' } & (
  | DisabledMissedCheckInStrategy
  | SetTimeOfDayMissedCheckInStrategy
);

type DisabledMissedCheckInCleanupStrategy = {
  type: 'DisabledMissedCheckInCleanupStrategy';
};

type EnabledMissedCheckInCleanupStrategy = {
  type: 'EnabledMissedCheckInCleanupStrategy';
};

export type ResourceMissedCheckInCleanupStrategyType = {
  key: 'MissedCheckInCleanupStrategy';
  properties: Record<string, unknown>;
} & (DisabledMissedCheckInCleanupStrategy | EnabledMissedCheckInCleanupStrategy);

type DisabledInviteSignInActionStrategy = {
  type: 'DisabledInviteSignInActionStrategy';
};

type FirstReservationOnlyInviteSignInActionStrategy = {
  type: 'FirstReservationOnlyInviteSignInActionStrategy';
};

type AllReservationsInviteSignInActionStrategy = {
  type: 'AllReservationsInviteSignInActionStrategy';
};

export type ResourceInviteSignInActionStrategyType = {
  key: 'InviteSignInActionStrategy';
  properties: Record<string, unknown>;
} & (
  | DisabledInviteSignInActionStrategy
  | FirstReservationOnlyInviteSignInActionStrategy
  | AllReservationsInviteSignInActionStrategy
);

type NotAssignableStrategy = {
  type: 'NotAssignableStrategy';
  properties: Record<string, unknown>;
};

type AssignableStrategy = {
  type: 'AssignableStrategy';
  properties: {
    maxAssignments: number;
  };
};

type ResourceAssignmentStrategyType = { key: 'AssignmentStrategy' } & (NotAssignableStrategy | AssignableStrategy);

type NotMappableStrategy = {
  type: 'NotMappableStrategy';
};

type PointMappingStrategy = {
  type: 'PointMappingStrategy';
};

type PolygonMappingStrategy = {
  type: 'PolygonMappingStrategy';
};

type ResourceMappingStrategyType = { key: 'MappingStrategy'; properties: Record<string, unknown> } & (
  | NotMappableStrategy
  | PointMappingStrategy
  | PolygonMappingStrategy
);

type PrivateReservationStrategy = {
  type: 'PrivateReservationStrategy';
};

type PublicReservationStrategy = {
  type: 'PublicReservationStrategy';
};

type ResourceDefaultReservationPrivacyStrategyType = {
  key: 'DefaultReservationPrivacyStrategy';
  properties: Record<string, unknown>;
} & (PrivateReservationStrategy | PublicReservationStrategy);

type DisabledCheckInStrategy = {
  type: 'DisabledCheckInStrategy';
  properties: Record<string, unknown>;
};

type DayOfCheckInStrategy = {
  type: 'DayOfCheckInStrategy';
  properties: Record<string, unknown>;
};

type TimeWindowCheckInStrategy = {
  type: 'TimeWindowCheckInStrategy';
  properties: {
    timeWindowMinutes: number;
  };
};

export type ResourceCheckInStrategyType = { key: 'CheckInStrategy' } & (
  | DisabledCheckInStrategy
  | DayOfCheckInStrategy
  | TimeWindowCheckInStrategy
);

export type ResourceTypeConfig = {
  id: string;
  name: string;
  description: string;
  'relation-id': string;
  'entity-id': string;
  settings: (
    | ResourceMissedCheckInStrategyType
    | ResourceMissedCheckInCleanupStrategyType
    | ResourceInviteSignInActionStrategyType
    | ResourceAssignmentStrategyType
    | ResourceMappingStrategyType
    | ResourceDefaultReservationPrivacyStrategyType
    | ResourceCheckInStrategyType
  )[];
  bookingRules: (
    | ResourceBookableRuleType
    | ResourceAssignmentRuleType
    | ResourceAdvancedBookingRuleType
    | ResourceBookingHourRuleType
    | ResourceReservationTypeRuleType
  )[];
};

export type ResourceAmenityType = {
  resourceTypeAmenityId: number;
  name: string;
  resourceTypeId: number;
  relationId: string;
};
